import React, { useMemo, useRef, useState } from 'react';
import {
  useUsersAndGroupsContext,
  useGetSession,
  UsersAndGroupsContextProvider,
} from '@opengov/bnp-core';
import {
  getSortedOptions,
  getUpdatedBulkStoryPermissionsOnOptionsSelected,
  removeSelectedSubjectStoryClaims,
  updateBulkSubjectStoryClaims,
} from '../../../stories/utils/subjectSelect';
import { ISubjectSelectOption } from '../../../types/subjectTypes';
import {
  ISubjectStoryClaimsList,
  IStoryPrivilege,
  IsubjectClaimsMessages,
  ISelectedSubjectList
} from '../../../types';
import { IStory } from '@opengov/stories-shared/types';
import { Map } from '@opengov/compose-app/immutable';
import { StoryGranularAccessDialog } from '../../../stories/components/StoryAccessGranularDialog/StoryAccessGranularDialog';
import { sanitizeSectionIdsForClaims } from '@opengov/stories-shared/utils';
import { useSaveSubjectStoryClaim } from '../../../stories/hooks/subjectClaims';

export interface IBulkStoryGranularAccessProps {
  closeModal(): void;
  entitySubdomain: string;
  selectedStories: Map<string, IStory>;
  messages: IsubjectClaimsMessages;
  publicationId: string;
  onSuccess?(): void;
}

export const BulkStoryGranularAccessView: React.FC<IBulkStoryGranularAccessProps> = (props) => {
  const { closeModal, selectedStories, messages, publicationId, onSuccess } = props;
  const saveAccessMutation = useSaveSubjectStoryClaim();
  const storyIds = selectedStories.keySeq().toArray();
  const storyTitles = selectedStories.valueSeq().map((story) => story.title).toArray();
  const sectionIds = selectedStories.valueSeq().map((story) => story.folderId).toArray();
  const [selectedSubjectStoryClaimsList, setSelectedSubjectStoryClaimsList] = useState<ISubjectStoryClaimsList>([]);
  const [selectedSubjectsList, setSelectedSubjectsList] = useState<ISelectedSubjectList>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputName = 'subjectSelect';

  const { activeUsers, groupWithMembers, usersMap, groupsMap } = useUsersAndGroupsContext();
  const { data: session } = useGetSession();

  const sortedOptionsList = useMemo(() => {
    return getSortedOptions({ session, users: activeUsers, groups: groupWithMembers, subjectStoryClaimsList: selectedSubjectStoryClaimsList });
  }, [session, activeUsers, groupWithMembers, selectedSubjectStoryClaimsList]);

  const onOptionsSelected = (options: ISubjectSelectOption[]) => {
    const [updatedSubjects, updatedClaims] = getUpdatedBulkStoryPermissionsOnOptionsSelected(selectedSubjectsList, selectedSubjectStoryClaimsList, options, storyIds);
    setSelectedSubjectsList(updatedSubjects);
    setSelectedSubjectStoryClaimsList(updatedClaims);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, subjectId: string) => {
    const privilege = e.target.name as IStoryPrivilege;
    const checked = e.target.checked;
    const { updatedSubjectsList, updatedSelectedClaims } = updateBulkSubjectStoryClaims(subjectId, privilege, checked, selectedSubjectsList, selectedSubjectStoryClaimsList);
    setSelectedSubjectsList(updatedSubjectsList);
    setSelectedSubjectStoryClaimsList(updatedSelectedClaims);
  };

  const handleClick = (subjectId: string) => {
    const { updatedSelectedSubjectsList, updatedSelectedSubjectStoryClaimsList } = removeSelectedSubjectStoryClaims(subjectId, selectedSubjectsList, selectedSubjectStoryClaimsList);
    setSelectedSubjectsList(updatedSelectedSubjectsList);
    setSelectedSubjectStoryClaimsList(updatedSelectedSubjectStoryClaimsList);
  };

  const handleSave = () => {
    void saveAccessMutation
      .mutateAsync({
        subjectStoryClaimsList: selectedSubjectStoryClaimsList,
        publicationId: publicationId,
        sectionIds: sanitizeSectionIdsForClaims(sectionIds),
        notifyBulkCollaborators: true
      })
      .then(() => onSuccess?.());
    closeModal();
  };

  const cols: { title: string; alignment: 'left' | 'center'; width: string }[] = [
    { title: messages.name, alignment: 'left', width: '50%' },
    { title: messages.view, alignment: 'center', width: '10%' },
    { title: messages.edit, alignment: 'center', width: '10%' },
    { title: messages.export, alignment: 'center', width: '10%' },
    { title: messages.publish, alignment: 'center', width: '10%' },
    { title: '', alignment: 'center', width: '10%' },
  ];

  return (
    <StoryGranularAccessDialog
      isBulk={true}
      storyTitles={storyTitles}
      inputRef={inputRef}
      inputName={inputName}
      messages={messages}
      sortedOptionsList={sortedOptionsList}
      cols={cols}
      subjectsToRender={selectedSubjectsList}
      usersMap={usersMap}
      groupsMap={groupsMap}
      onClose={closeModal}
      onSave={handleSave}
      onOptionChange={(_, value) => onOptionsSelected(value)}
      onPrivilegeChange={handleChange}
      onRemoveClick={handleClick}
    />
  );
};

export const BulkStoryGranularAccess = (props: IBulkStoryGranularAccessProps) => {
  return (
    <UsersAndGroupsContextProvider entitySubdomain={props.entitySubdomain}>
      <BulkStoryGranularAccessView {...props} />
    </UsersAndGroupsContextProvider>
  );
};
